import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Gift, Copy, Check, Users, Share2 } from "lucide-react";

export const ReferralCard = () => {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/referrals/me").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return null;

  const link = `${window.location.origin}/register?ref=${data.code}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = link;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const share = () => {
    if (navigator.share) {
      navigator.share({
        title: "GalerieCop — event photo sharing",
        text: "I use GalerieCop to collect guest photos at events. Join with my link:",
        url: link,
      }).catch(() => {});
    } else {
      copy();
    }
  };

  return (
    <div data-testid="referral-card" className="bg-white rounded-2xl soft-shadow p-5 flex flex-wrap items-center gap-4">
      <div className="w-11 h-11 rounded-full bg-coral-100 flex items-center justify-center shrink-0">
        <Gift className="w-5 h-5 text-coral-400" />
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="font-semibold text-navy-700">Refer a friend</h3>
        <p className="text-sm text-navy-700/60">
          Every {data.rewards?.needed || 3} sign-ups earn you {data.rewards?.discount_pct || 10}% off any tier <b>plus</b> one free reel-branding unlock — auto-applied.
        </p>
        {data.rewards?.available > 0 || data.rewards?.reel_available > 0 ? (
          <div data-testid="referral-reward-badge" className="mt-1.5 flex flex-wrap gap-1.5">
            {data.rewards?.available > 0 && (
              <span data-testid="referral-reward-discount" className="inline-flex items-center gap-1.5 text-xs font-semibold bg-green-50 text-green-700 rounded-full px-2.5 py-1">
                <Gift className="w-3.5 h-3.5" />
                {data.rewards.available} × {data.rewards.discount_pct}% off
              </span>
            )}
            {data.rewards?.reel_available > 0 && (
              <span data-testid="referral-reward-reel" className="inline-flex items-center gap-1.5 text-xs font-semibold bg-coral-100 text-coral-500 rounded-full px-2.5 py-1">
                <Gift className="w-3.5 h-3.5" />
                {data.rewards.reel_available} × reel unlock
              </span>
            )}
          </div>
        ) : (
          <div data-testid="referral-reward-progress" className="mt-1.5 text-xs text-navy-700/50">
            {data.rewards ? `${data.rewards.progress} of ${data.rewards.needed} sign-ups toward your next reward (${data.rewards.discount_pct}% off + free reel unlock)` : ""}
          </div>
        )}
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <code data-testid="referral-link" className="text-xs bg-cream-100 border border-cream-200 rounded-md px-2.5 py-1.5 text-navy-700/80 truncate max-w-[320px]">{link}</code>
          <button onClick={copy} data-testid="referral-copy-btn" className="inline-flex items-center gap-1.5 text-xs font-semibold border border-navy-700/15 rounded-full px-3 py-1.5 text-navy-700 hover:bg-cream-100">
            {copied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
            {copied ? "Copied" : "Copy"}
          </button>
          <button onClick={share} data-testid="referral-share-btn" className="inline-flex items-center gap-1.5 text-xs font-semibold bg-coral-300 text-white rounded-full px-3 py-1.5 hover:bg-coral-400">
            <Share2 className="w-3.5 h-3.5" /> Share
          </button>
        </div>
      </div>
      <div data-testid="referral-count" className="flex items-center gap-2 bg-cream-100 rounded-xl px-4 py-3">
        <Users className="w-4 h-4 text-coral-400" />
        <div>
          <div className="text-xl font-bold text-navy-700 leading-none">{data.count}</div>
          <div className="text-[10px] uppercase tracking-wider text-navy-700/50 mt-0.5">Sign-ups</div>
        </div>
      </div>
    </div>
  );
};

export default ReferralCard;
